import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Wallet, TrendingUp, Clock, Info, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import SectionBand from '@/components/ui/SectionBand';

const RESERVE_BALANCE_EXPLANATION =
  "This is money you’ve put into Reserve—from top-ups or your automatic plan. Use it toward a trip to lower what’s left to pay (and sometimes your payment size or how many payments you have). It doesn’t replace your regular schedule. Putting Reserve toward a trip is just for tracking—it doesn’t charge your card, and you can change or undo it anytime. Any extra, or money back from canceled plans, stays here and can go toward any trip.";

export default function ReserveBalanceCard({ 
  balanceCents, 
  totalAllocatedCents,
  updatedAt, 
  isLoading, 
  onAddFunds 
}) {
  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(cents / 100);
  };

  const formattedBalance = formatCurrency(balanceCents || 0);
  const allocated = totalAllocatedCents ?? 0;
  const availableToAllocate = Math.max(0, (balanceCents || 0) - allocated);
  const lastUpdated = updatedAt
    ? formatDistanceToNow(new Date(updatedAt), { addSuffix: true })
    : 'just now';

  const [showInfo, setShowInfo] = useState(false);
  const infoRef = useRef(null);
  const overlayRef = useRef(null);
  const [overlayStyle, setOverlayStyle] = useState({});

  useEffect(() => {
    if (!showInfo || !infoRef.current) return;
    const rect = infoRef.current.getBoundingClientRect();
    setOverlayStyle({
      position: 'fixed',
      left: rect.left,
      top: rect.bottom + 8,
      zIndex: 9999
    });
  }, [showInfo]);

  useEffect(() => {
    if (!showInfo) return;
    const handleClickOutside = (e) => {
      const inTrigger = infoRef.current?.contains(e.target);
      const inOverlay = overlayRef.current?.contains(e.target);
      if (!inTrigger && !inOverlay) setShowInfo(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showInfo]);

  return (
    <div className="h-full rounded-2xl sm:rounded-3xl bg-white/[0.06] backdrop-blur-xl border border-white/10 relative overflow-hidden flex flex-col shadow-[0_0_0_1px_rgba(255,255,255,0.03)_inset]">
      {/* Ambient glow swapped from emerald → brand royal blue (hsl 220) so the
          balance card threads into the Reserve page palette and matches the
          progress-bar / "Next" badge accent used elsewhere. */}
      <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-[hsl(228,40%,38%)]/[0.07] -translate-y-1/2 translate-x-1/2 blur-2xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/[0.03] translate-y-1/2 -translate-x-1/2 blur-2xl pointer-events-none" />

      <SectionBand
        icon={Wallet}
        iconColor="text-[hsl(228,35%,58%)]"
        accentRgb="10, 18, 51"
        title="Reserve Balance"
        rightSlot={
          <div className="relative" ref={infoRef}>
            <button
              type="button"
              onClick={() => setShowInfo(!showInfo)}
              className="p-1 rounded-full text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors focus:outline-none focus:ring-1 focus:ring-white/30"
              aria-label="What is Reserve balance?"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
            {showInfo &&
              createPortal(
                <div
                  ref={overlayRef}
                  role="dialog"
                  aria-label="Reserve balance explanation"
                  className="min-w-72 max-w-md rounded-xl border border-white/10 bg-[#0a0a0a] p-4 shadow-xl"
                  style={overlayStyle}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-white/85 text-sm font-normal leading-relaxed">
                      {RESERVE_BALANCE_EXPLANATION}
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowInfo(false)}
                      className="flex-shrink-0 p-0.5 rounded-full text-white/40 hover:text-white/70 focus:outline-none"
                      aria-label="Close"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>,
                document.body
              )}
          </div>
        }
      />

      <div className="relative z-10 flex flex-col flex-1 p-6 sm:p-8 pt-5 sm:pt-6">
        <div className="flex-1 flex flex-col justify-center">
          {isLoading ? (
            <div className="h-14 w-52 bg-white/10 rounded-xl animate-pulse" />
          ) : (
            <div className="text-4xl md:text-5xl font-light tracking-tight text-white tabular-nums">
              {formattedBalance}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2 text-white/40 text-sm">
            <span>Available for booking</span>
            <span className="text-white/20">·</span>
            <Clock className="w-3 h-3" />
            <span className="text-xs">Updated {lastUpdated}</span>
          </div>
          {allocated > 0 && (
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-white/35">
              <span>Allocated to trips: {formatCurrency(allocated)}</span>
              <span>·</span>
              <span>Unallocated: {formatCurrency(availableToAllocate)}</span>
            </div>
          )}
        </div>

        <div className="mt-4 sm:mt-6">
          <Button
            onClick={onAddFunds}
            className="w-full bg-white text-black hover:bg-white/95 rounded-full py-4 sm:py-5 text-sm sm:text-base font-medium transition-all hover:shadow-lg hover:shadow-white/5"
          >
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Add Funds
          </Button>
        </div>
      </div>
    </div>
  );
}
